/**
 * PartyCard - Collapsible card for party entry
 *
 * Used in PartiesForm to display and edit individual party information
 * with support for address entry, contact details, and professional IDs.
 */

import { ChevronDown, ChevronUp, Trash2, User } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { Party, PartyType } from '../../../types';
import { type Address, AddressForm } from './AddressForm';

export interface PartyCardProps {
  /** The party data */
  party: Party;
  /** Index in the list */
  index: number;
  /** Change handler */
  onChange: (party: Party) => void;
  /** Delete handler */
  onDelete: () => void;
  /** Whether the card can be deleted */
  canDelete?: boolean;
  /** Field-level errors */
  errors?: Partial<Record<keyof Party | 'address', string>>;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Initially expanded */
  defaultExpanded?: boolean;
}

/** Party type options with labels */
const PARTY_TYPE_OPTIONS: { value: PartyType; label: string; requiresLicense?: boolean }[] = [
  { value: 'borrower', label: 'Borrower' },
  { value: 'seller', label: 'Seller' },
  { value: 'lender', label: 'Lender', requiresLicense: true },
  { value: 'mortgage_broker', label: 'Mortgage Broker', requiresLicense: true },
  { value: 'real_estate_broker_buyer', label: "Buyer's Real Estate Broker", requiresLicense: true },
  {
    value: 'real_estate_broker_seller',
    label: "Seller's Real Estate Broker",
    requiresLicense: true,
  },
  { value: 'settlement_agent', label: 'Settlement Agent', requiresLicense: true },
];

/** Get display label for party type */
function getPartyTypeLabel(type: PartyType): string {
  return PARTY_TYPE_OPTIONS.find((opt) => opt.value === type)?.label ?? type;
}

/** Check if party type requires license info */
function requiresLicenseInfo(type: PartyType): boolean {
  return PARTY_TYPE_OPTIONS.find((opt) => opt.value === type)?.requiresLicense ?? false;
}

/**
 * PartyCard - Collapsible card for individual party
 */
export const PartyCard: React.FC<PartyCardProps> = ({
  party,
  index,
  onChange,
  onDelete,
  canDelete = true,
  errors = {},
  disabled = false,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const handleFieldChange = <K extends keyof Party>(field: K, value: Party[K]) => {
    onChange({ ...party, [field]: value });
  };

  const handleAddressChange = (address: Address) => {
    onChange({
      ...party,
      address_street: address.street,
      address_city: address.city,
      address_state: address.state,
      address_zip_code: address.zipCode,
    });
  };

  const address: Address = {
    street: party.address_street ?? '',
    city: party.address_city ?? '',
    state: party.address_state ?? '',
    zipCode: party.address_zip_code ?? '',
  };

  const showLicenseFields = requiresLicenseInfo(party.party_type);

  return (
    <div
      className={cn(
        'border rounded-lg overflow-hidden',
        errors.name || errors.party_type ? 'border-destructive/50' : 'border-border',
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3 cursor-pointer',
          'bg-muted hover:bg-muted/80 transition-colors',
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <User className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="font-medium text-foreground">
              {party.name || `Party ${index + 1}`}
              {party.is_primary && (
                <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                  Primary
                </span>
              )}
            </p>
            <p className="text-sm text-muted-foreground">{getPartyTypeLabel(party.party_type)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {canDelete && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-background">
          {/* Party Type and Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor={`party-${index}-type`}>
                Party Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={party.party_type}
                onValueChange={(v) => handleFieldChange('party_type', v as PartyType)}
                disabled={disabled}
              >
                <SelectTrigger
                  id={`party-${index}-type`}
                  className={cn(errors.party_type && 'border-destructive')}
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PARTY_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.party_type && <p className="text-sm text-destructive">{errors.party_type}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`party-${index}-name`}>
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`party-${index}-name`}
                type="text"
                value={party.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Full name or company name"
                disabled={disabled}
                className={cn(errors.name && 'border-destructive')}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
          </div>

          {/* Primary checkbox for borrower/seller */}
          {(party.party_type === 'borrower' || party.party_type === 'seller') && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`party-${index}-primary`}
                checked={party.is_primary ?? false}
                onChange={(e) => handleFieldChange('is_primary', e.target.checked)}
                disabled={disabled}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <Label htmlFor={`party-${index}-primary`} className="font-normal cursor-pointer">
                Primary {party.party_type}
              </Label>
            </div>
          )}

          {/* Address */}
          <div className="pt-2">
            <h4 className="text-sm font-medium text-foreground mb-2">Address</h4>
            <AddressForm
              value={address}
              onChange={handleAddressChange}
              idPrefix={`party-${index}-address`}
              disabled={disabled}
              compact
            />
          </div>

          {/* Contact Information */}
          <div className="pt-2">
            <h4 className="text-sm font-medium text-foreground mb-2">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor={`party-${index}-email`}>Email</Label>
                <Input
                  id={`party-${index}-email`}
                  type="email"
                  value={party.email ?? ''}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder="email@example.com"
                  disabled={disabled}
                  className={cn(errors.email && 'border-destructive')}
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`party-${index}-phone`}>Phone</Label>
                <Input
                  id={`party-${index}-phone`}
                  type="tel"
                  value={party.phone ?? ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  placeholder="(555) 555-5555"
                  disabled={disabled}
                  className={cn(errors.phone && 'border-destructive')}
                />
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* License Information (for professionals) */}
          {showLicenseFields && (
            <div className="pt-2">
              <h4 className="text-sm font-medium text-foreground mb-2">
                Professional License Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor={`party-${index}-license`}>License ID</Label>
                  <Input
                    id={`party-${index}-license`}
                    type="text"
                    value={party.license_id ?? ''}
                    onChange={(e) => handleFieldChange('license_id', e.target.value)}
                    placeholder="State license number"
                    disabled={disabled}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`party-${index}-nmls`}>NMLS #</Label>
                  <Input
                    id={`party-${index}-nmls`}
                    type="text"
                    value={party.nmls ?? ''}
                    onChange={(e) => handleFieldChange('nmls', e.target.value)}
                    placeholder="NMLS identifier"
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PartyCard.displayName = 'PartyCard';

export { PARTY_TYPE_OPTIONS, getPartyTypeLabel, requiresLicenseInfo };
